import { getPhotographyBrief_structuredBrief_fields_SmartShootPhotographyFields as PhotographyFields } from '__generated__/getPhotographyBrief';
import { getPhotographyBriefWithPaymentData_smartShootPaymentData as PhotographyPaymentData } from '__generated__/getPhotographyBriefWithPaymentData';
import { getVideoBrief_structuredBrief_fields_SmartShootVideoFields as VideoFields } from '__generated__/getVideoBrief';
import { getVideoBriefWithPaymentData_smartShootPaymentData as VideoPaymentData } from '__generated__/getVideoBriefWithPaymentData';
import { Industry } from '__generated__/Industry';
import { Currency, toUnit } from 'common/utils/currency';
import moment from 'moment'; // tslint:disable-line:import-blacklist
import Card from 'projects/components/Card';
import React from 'react';
import { FormattedNumber } from 'react-intl';

interface Props {
    briefFields: VideoFields | PhotographyFields;
    industries: Industry[];
    smartShootPaymentData: VideoPaymentData | PhotographyPaymentData;
}

function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export default class BriefPaymentContent extends React.Component<Props> {
    getIndustryTitle(industryKey: string) {
        const { industries } = this.props;

        for (const item in industries) {
            const industry = industries[item];

            if (industry.key === industryKey) {
                return industry.title;
            }
        }
    }
    render() {
        const { briefFields, smartShootPaymentData } = this.props;
        const currency: Currency = (Currency as any)[smartShootPaymentData.currency];
        return (
            <div className="content-setting">
                <Card variations={['field', 'smartshoot-payment']}>
                    <Card.Block>
                        <div className="card-summary-list">
                            <BriefPaymentLogos />
                            <BriefPaymentSection label="Project details">
                                <BriefPaymentField label="Project name">
                                    {briefFields.title}
                                </BriefPaymentField>
                                <BriefPaymentField label="Description">
                                    {briefFields.description}
                                </BriefPaymentField>
                                <BriefPaymentField label="Inspiration">
                                    {briefFields.inspirationUrls.length > 0 &&
                                        briefFields.inspirationUrls.map(url => (
                                            <div key={url}>{url}</div>
                                        ))}
                                </BriefPaymentField>
                                {isVideoFields(briefFields) && (
                                    <BriefPaymentField label="Call to Actions">
                                        {briefFields.postVideoCta}
                                    </BriefPaymentField>
                                )}
                            </BriefPaymentSection>
                            <BriefPaymentSection
                                label={isVideoFields(briefFields) ? 'Video shoot' : 'Photo shoot'}
                            >
                                <BriefPaymentField label="Address">
                                    {[
                                        briefFields.address,
                                        briefFields.city,
                                        briefFields.state,
                                        briefFields.country,
                                        briefFields.zipCode,
                                    ].join(', ')}
                                </BriefPaymentField>
                                <BriefPaymentField label="Description">
                                    {briefFields.locationName}
                                </BriefPaymentField>
                                {!briefFields.deferred && (
                                    <>
                                        <BriefPaymentField label="Date">
                                            {/* e.g. Thursday, 21st February, 2019 */}
                                            {moment(briefFields.datetime).format(
                                                'dddd, Do MMMM, YYYY'
                                            )}
                                        </BriefPaymentField>
                                        <BriefPaymentField label="Time">
                                            {/* e.g. 6:00 am */}
                                            {moment(briefFields.datetime).format('h:mm a')}
                                        </BriefPaymentField>
                                    </>
                                )}
                                {!isVideoFields(briefFields) && (
                                    <BriefPaymentField label="Indoors or Outdoors">
                                        {capitalize(briefFields.indoorsOrOutdoors)}
                                    </BriefPaymentField>
                                )}
                            </BriefPaymentSection>
                            <BriefPaymentSection label="Contact information">
                                <BriefPaymentField label="First name">
                                    {briefFields.firstName}
                                </BriefPaymentField>
                                <BriefPaymentField label="Last name">
                                    {briefFields.lastName}
                                </BriefPaymentField>
                                <BriefPaymentField label="Email">
                                    {briefFields.email}
                                </BriefPaymentField>
                                <BriefPaymentField label="Phone">
                                    {briefFields.phone}
                                </BriefPaymentField>
                                <BriefPaymentField label="Business name">
                                    {briefFields.businessName}
                                </BriefPaymentField>
                                <BriefPaymentField label="Industry">
                                    {this.getIndustryTitle(briefFields.industry)}
                                </BriefPaymentField>
                                <BriefPaymentField label="Other details">
                                    {briefFields.other}
                                </BriefPaymentField>
                            </BriefPaymentSection>
                            <BriefPaymentSection label="Pricing">
                                <div className="payment-summary">
                                    <div>{capitalize(briefFields.package)} package:</div>
                                    <div>
                                        <FormattedNumber
                                            value={toUnit(smartShootPaymentData.amount, currency)}
                                            style="currency"
                                            currency={smartShootPaymentData.currency}
                                            minimumFractionDigits={2}
                                        />
                                    </div>
                                </div>
                                <div className="payment-summary payment-summary--total">
                                    <div>Total:</div>
                                    <div>
                                        <FormattedNumber
                                            value={toUnit(smartShootPaymentData.amount, currency)}
                                            style="currency"
                                            currency={smartShootPaymentData.currency}
                                            minimumFractionDigits={2}
                                        />
                                    </div>
                                </div>
                            </BriefPaymentSection>
                        </div>
                    </Card.Block>
                </Card>
            </div>
        );
    }
}

interface FieldProps {
    label: string;
}

const BriefPaymentField: React.FunctionComponent<FieldProps> = props => {
    if (!props.children) return null;

    return (
        <div className="paragraph">
            <strong>{props.label}</strong>
            <div>{props.children}</div>
        </div>
    );
};

interface SectionProps {
    label: string;
}

const BriefPaymentSection: React.FunctionComponent<SectionProps> = props => (
    <div className="card-summary-list__item">
        <div className="heading heading--h4">{props.label}</div>
        {props.children}
    </div>
);

const BriefPaymentLogos: React.FunctionComponent = () => (
    <div className="card-summary-list__item">
        <div className="spaced-items">
            <div className="spaced-items__item">
                <img src={require(`images/99designs-wordmark.svg`)} style={{ width: 135 }} />
            </div>
            <div className="spaced-items__item">
                <img
                    className="smartshoot-logo"
                    src={require(`images/smartshoot/ss-logo-grey.png`)}
                />
            </div>
        </div>
    </div>
);

function isVideoFields(briefFields: VideoFields | PhotographyFields): briefFields is VideoFields {
    return (briefFields as VideoFields).postVideoCta !== undefined;
}
